/**
 * Utility functions for building components on the whiteboard.
 *
 * This module provides functions to build frames, shapes, and other components
 * on the whiteboard. It includes builders for specific object types and a
 * general component builder that can handle nested objects.
 *
 * The base object builders are responsible for normalizing data. For example,
 * the builder may need to convert width and height to scale, depending on the
 * object type. The goal is to keep components as easy as possible to define, as
 * this may be used by external applications (like the roadmap build in aha-app)
 */

import components from '../objects/components';
import Frame from '../objects/Frame';
import Shape from '../objects/Shape';
import type WhiteboardController from '../controllers/WhiteboardController';
import { type IPoint } from 'fabric/fabric-impl';
import { type ShapeName } from '../shapes';

// Base options that all builders share
interface BaseBuilderOptions {
  left?: number;
  top?: number;
  [key: string]: any;
}

// Base builder arguments that all builders share
interface BaseBuilderArgs<T extends BaseBuilderOptions> {
  controller: WhiteboardController;
  options: T;
  componentName?: string;
}

// Frame-specific options
interface FrameBuilderOptions extends BaseBuilderOptions {
  width?: number;
  height?: number;
  scaleX?: number;
  scaleY?: number;
}

// Shape-specific options
interface ShapeBuilderOptions extends BaseBuilderOptions {
  shapeName: ShapeName;
}

// Type guard for builders
type BuilderType = 'frame' | 'shape';

// Generic builder function type
type BuilderFunction<T extends BaseBuilderOptions, R> = (
  args: BaseBuilderArgs<T>
) => R;

// Parent object interface
interface ParentObject {
  left: number;
  top: number;
  type?: string;
  id?: string;
  [key: string]: any;
}

// Object builder specific interfaces
export interface ObjectBuilderObject {
  type: BuilderType;
  options: BaseBuilderOptions;
  id?: string;
  container?: string;
  children?: ComponentTemplate[];
  [key: string]: any;
}

interface ObjectBuilderArgs {
  controller: WhiteboardController;
  object: ObjectBuilderObject;
  pointer: IPoint;
  parent?: ParentObject;
  componentName?: string;
}

// Component interfaces
export interface ComponentTemplate {
  type: BuilderType;
  options: Record<string, any>;
  children?: ComponentTemplate[];
  [key: string]: any;
}

export interface ComponentBuilderArgs {
  controller: WhiteboardController;
  componentName: string;
  pointer: IPoint;
  parent?: ParentObject;
}

// Builder implementations
const frameBuilder: BuilderFunction<FrameBuilderOptions, Frame> = ({
  controller,
  options,
  componentName = '',
}) => {
  if (options.width) {
    options.scaleX = options.width / 400;
    options.width = 400;
  }

  if (options.height) {
    options.scaleY = options.height / 500;
    options.height = 500;
  }

  return new Frame({
    controller,
    fill: '#FFFFFF',
    ...options,
    width: Frame.defaultWidth(),
    height: Frame.defaultHeight(),
    strokeWidth: 0,
    componentName,
  });
};

const shapeBuilder: BuilderFunction<ShapeBuilderOptions, Shape> = ({
  controller,
  options,
}) => {
  if (!options.shapeName) {
    throw new Error('Shape must specify shapeName is required');
  }

  return new Shape({
    controller,
    stroke: '#BDBDBD',
    fill: '#FFFFFF',
    strokeWidth: 2,
    ...options,
  });
};

const builderMap: Record<BuilderType, BuilderFunction<any, any>> = {
  frame: frameBuilder,
  shape: shapeBuilder,
};

export const objectBuilder = ({
  controller,
  object,
  pointer,
  parent,
  componentName,
}: ObjectBuilderArgs) => {
  const { type, options } = object;

  // Normalize general params
  options.left = options.left ?? 0;
  options.top = options.top ?? 0;

  if (parent) {
    // Shift object to parent location
    options.left += parent.left;
    options.top += parent.top;
  } else {
    // Shift object to pointer location
    options.left += pointer.x;
    options.top += pointer.y;
  }

  // If this object is in a frame, set the container field. This is important for
  // inclusion in the parent frame
  if (parent && parent.type === 'frame') {
    object.container = parent.id;
  }

  const builder = builderMap[type];
  if (!builder) {
    throw new Error(`Unknown object type: ${type}`);
  }

  const whiteboardObject = builder({ controller, options, componentName });

  // Avoid laying out autolayouts until objects are added. Nested layouts will
  // grab grandchildren if layout is enabled at the time of creation
  if (options.layoutEnabled) {
    whiteboardObject.layoutEnabled = false;
  }

  controller.addToCanvas(whiteboardObject);

  return whiteboardObject;
};

export const componentBuilder = ({
  controller,
  componentName,
  pointer,
  parent,
}: ComponentBuilderArgs): fabric.Object => {
  const componentTemplate = components[componentName];
  if (!componentTemplate) {
    throw new Error(`Unknown component type: ${componentName}`);
  }

  const newParent = objectBuilder({
    controller,
    object: {
      ...componentTemplate,
      // Avoid laying out autolayouts until objects are added. Nested layouts will
      // grab grandchildren if layout is enabled at the time of creation
      options: { ...componentTemplate.options, layoutEnabled: false },
    },
    pointer,
    parent,
    componentName,
  });

  componentTemplate.children?.forEach(child => {
    // Create a clean copy of each child's options
    const childCopy: ObjectBuilderObject = {
      ...child,
      options: { ...child.options },
    };

    const childComponent = components[childCopy.type];
    if (childComponent) {
      componentBuilder({
        controller,
        componentName: childCopy.type,
        pointer,
        parent: newParent,
      });
    } else {
      objectBuilder({
        controller,
        object: childCopy,
        pointer,
        parent: newParent,
      });
    }
  });

  // Once all children are added, re-enable layout
  if (componentTemplate.options.layoutEnabled) {
    newParent.set('layoutEnabled', true);
  }

  return newParent;
};
